import { Button, Typography } from '@material-ui/core'
import { Component, ErrorInfo, ReactNode } from 'react'

import { getTranslate } from '../../localization'

type ErrorBoundaryProps = {
  children: ReactNode
  // Changing this clears the error. panelRoutes passes the resolved pathname, because the
  // <Route> key is the route PATTERN — so navigating between two instances of the same
  // parameterised route (crashed item A -> item B) reconciles the existing boundary rather
  // than unmounting it, and a stale error would survive a navigation that visibly changed
  // the page. Resetting on the prop rather than keying the subtree avoids remounting a
  // healthy screen just because its URL changed.
  resetKey?: string
}

type ErrorBoundaryState = {
  error: Error | null
  resetKey?: string
}

/*
 * The fallback must not be able to throw.
 *
 * This is the only boundary in the tree, so an exception raised while rendering the fallback
 * propagates past it with nothing above to catch — reproducing the blank page this component
 * exists to prevent, and doing it in the one code path nobody would think to test. That is not
 * hypothetical here: if the localization module is what failed in the first place, calling
 * getTranslate again is the most likely way to hit it twice.
 *
 * So translation is best-effort and the English literal is the guaranteed floor.
 */
const safeTranslate = (text: string): string => {
  try {
    return getTranslate(text) || text
  } catch {
    return text
  }
}

/**
 * Contains a render error to one screen instead of blanking the whole panel.
 *
 * Without a boundary anywhere in the tree, React 16+ unmounts EVERYTHING when an error
 * reaches the root — the drawer, the navigation, the lot. The user gets a white page with no
 * message, no way back, and nothing to report beyond "the screen went white". That is a real
 * QA row on this project («صفحه سفید در حین وارد کردن مقدار در فیلتر پروموشن ها» — white
 * screen while typing in the promotions filter), and it is unactionable precisely because the
 * blank page carries no information.
 *
 * `observability.md` § Client-Side asks for exactly this: "Wrap screen-level components in
 * React Error Boundaries that capture and report errors before showing a fallback UI."
 *
 * WHAT THIS DOES NOT DO: it does not fix any crash. It converts an unreportable blank page
 * into a legible failure plus a console stack, which is the difference between a bug someone
 * can file and one they cannot. Treat a boundary firing as a defect to chase, never as the
 * defect being handled.
 *
 * Placement is PER ROUTE (panelRoutes), not at the app root, so the drawer survives and the
 * user can navigate away.
 *
 * Navigating to a DIFFERENT route pattern unmounts this component, which resets it. Navigating
 * between two instances of the SAME pattern does not — React reconciles the existing instance
 * because the <Route> key is the pattern, not the resolved path — so `resetKey` carries the
 * pathname and clears the error explicitly. An earlier version of this comment claimed
 * unmounting covered both cases; it does not.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null, resetKey: props.resetKey }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  // Clears the error when the caller says the context changed. Without this the boundary has
  // NO reset at all: navigating between two instances of the same parameterised route keeps
  // the component mounted, so the user would be stuck looking at the failure of a page they
  // already left.
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey }
    }
    return null
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Console, not a toast: the audience is a developer with devtools open, and the toast
    // path may itself be part of what just failed. There is no crash reporter wired into
    // this project yet — when one is added, report from here.
    //
    // componentStack is the part worth keeping: the raw Error alone rarely says which screen
    // was rendering when it threw.
    // eslint-disable-next-line no-console
    console.error('[screen] render failed', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    const { children } = this.props

    if (!error) {
      return children
    }

    return (
      <div
        role="alert"
        style={{
          padding: 24,
          display: 'grid',
          gap: 12,
          justifyItems: 'start'
        }}>
        <Typography variant="h4">
          {safeTranslate('This page could not be displayed.')}
        </Typography>
        <Typography variant="body1">
          {safeTranslate(
            'The rest of the panel still works — use the menu to go elsewhere, or try again.'
          )}
        </Typography>
        {/* A full reload rather than a state reset: whatever state produced the crash is
            still in memory, so clearing `error` alone would re-render straight back into it. */}
        <Button
          color="primary"
          variant="contained"
          onClick={() => window.location.reload()}>
          {safeTranslate('Try again')}
        </Button>
      </div>
    )
  }
}

export default ErrorBoundary
